<script setup>
import { useGenerateImageVariant } from "@core/composable/useGenerateImageVariant";
import authV2LoginIllustrationBorderedDark from "@images/pages/auth-v2-login-illustration-bordered-dark.png";
import authV2LoginIllustrationBorderedLight from "@images/pages/auth-v2-login-illustration-bordered-light.png";
import authV2LoginIllustrationDark from "@images/pages/auth-v2-login-illustration-dark.png";
import authV2LoginIllustrationLight from "@images/pages/auth-v2-login-illustration-light.png";
import authV2MaskDark from "@images/pages/misc-mask-dark.png";
import authV2MaskLight from "@images/pages/misc-mask-light.png";
import { VNodeRenderer } from "@layouts/components/VNodeRenderer";
import { themeConfig } from "@themeConfig";
import { useApiFront } from "@/composables/useApi";
import { withQuery } from "ufo";

definePage({ meta: { layout: "blank" } });

// Router
const route = useRoute();
const router = useRouter();

const removeTokenOnLoginPage = async (e) => {
  try {
    useCookie("userData").value = null;
    useCookie("accessToken").value = null;
    useCookie("refreshToken").value = null;
    useCookie("userMallName").value = null;
  } catch (err) {
    console.error(err);
  }
};

watchEffect(() => removeTokenOnLoginPage());

const refVForm = ref();
const form = ref({
  username: "",
  password: "",
  mallId: "",
  remember: false,
});

const valid = ref("");
const errors = ref("");

const login = async (e) => {
  const baseURL = import.meta.env.VITE_API_BASE_URL;
  try {
    const res = await $api("/cms/auth/login", {
      method: "POST",
      body: {
        loginId: e.username,
        password: e.password,
        mallId: e.mallId,
      },
      onResponseError({ response }) {
        errors.value = response._data.message;

        setTimeout(() => (errors.value = false), 10000);
      },
    });

    if (res.statusCode === 200) {
      useCookie("userData").value = res.data;
      useCookie("accessToken").value = res.data.accessToken;
      useCookie("refreshToken").value = res.data.refreshToken;

      const { data: dataMall } = await useApi(
        withQuery("/cms/mall/" + e.mallId, {
          method: "GET",
        }),
      );

      useCookie("userMallName").value = dataMall.value?.data.name;

      errors.value = null;
      valid.value = res.message;
      errors.value = false;
      router.replace(route.query.to ? String(route.query.to) : "/");
    }
  } catch (err) {
    errors.value = err._data.message;

    setTimeout(() => (errors.value = false), 10000);
  }
};

const onSubmit = (e) => {
  load();
  refVForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid) login(e);
  });
};

const isPasswordVisible = ref(false);
const authThemeImg = useGenerateImageVariant(
  authV2LoginIllustrationLight,
  authV2LoginIllustrationDark,
  authV2LoginIllustrationBorderedLight,
  authV2LoginIllustrationBorderedDark,
  true,
);
const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark);

const loading = ref();

const load = () => {
  loading.value = true;
  setTimeout(() => {
    loading.value = false;
  }, 3000);
};

/*
 * Data Mall
 * */

const mallQueries = [];
const { data: mallQuery } = await useApiFront(withQuery("/cms/mall/all"));
mallQuery.value?.data.forEach((item) => {
  mallQueries.push({
    title: item.name,
    value: item.id,
  });
});
</script>

<template>
  <div class="auth-bg"></div>
  <VRow class="auth-wrapper bg-surface" no-gutters>
    <VCol
      class="auth-card d-flex align-center justify-center mx-auto"
      cols="12"
      lg="3"
      sm="4"
    >
      <VCard :max-width="500" border class="mt-12 mt-sm-0 pa-4">
        <VCardText>
          <VNodeRenderer
            :nodes="themeConfig.app.logo"
            class="mb-6 w-75 mx-auto"
          />
          <h5 class="text-h5 mb-1">Welcome to omipos e-Money</h5>
          <p class="mb-0">Please sign in to your account to begin</p>
        </VCardText>
        <VCardText>
          <VAlert v-show="valid" class="mb-4" type="success">
            {{ valid }}
          </VAlert>
          <VAlert v-show="errors" class="mb-4" type="error">
            {{ errors }}
          </VAlert>
          <VForm
            ref="refVForm"
            @submit.prevent="
              () => {
                onSubmit(form);
              }
            "
          >
            <VRow>
              <!-- loginId -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.username"
                  :rules="[requiredValidator]"
                  autofocus
                  label="Login ID"
                  placeholder="Input Login ID"
                  type="text"
                />
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.password"
                  :append-inner-icon="
                    isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'
                  "
                  :rules="[requiredValidator]"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  label="Password"
                  placeholder="Input Password"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
              </VCol>

              <!-- mallId -->
              <VCol cols="12">
                <AppSelect
                  v-model="form.mallId"
                  :items="mallQueries"
                  :rules="[requiredValidator]"
                  label="Mall *"
                  placeholder="Choose Mall"
                />
              </VCol>

              <VCol cols="12">
                <div
                  class="d-flex align-center flex-wrap justify-space-between mt-2 mb-4"
                >
                  <VCheckbox v-model="form.remember" label="Remember me" />
                  <RouterLink
                    :to="{ name: 'forgot-password' }"
                    class="text-primary ms-2 mb-1"
                  >
                    Forgot Password?
                  </RouterLink>
                </div>

                <VBtn
                  :disabled="loading"
                  :loading="loading"
                  block
                  type="submit"
                >
                  Login

                  <template #loader>
                    <span class="custom-loader">
                      <VIcon class="text-white" icon="tabler-refresh" />
                    </span>
                  </template>
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>
